import { useId } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@blimo/ui';

/**
 * Content card of a page (shadcn dashboard style): optional header strip (title 14 px semibold, description 13 px,
 * action on the right), body with 16 px padding (`flush` → no padding, for lists and tables), optional footer note.
 * `plain`: no card – just the heading row above the children (e.g. a grid of KPI cards).
 * With `title` the section is a region labelled by its heading; otherwise `label` names it.
 *
 * @param {{
 *   title?: import('react').ReactNode,
 *   description?: import('react').ReactNode,
 *   action?: import('react').ReactNode,
 *   footer?: import('react').ReactNode,
 *   children: import('react').ReactNode,
 *   className?: string,
 *   bodyClassName?: string,
 *   flush?: boolean,
 *   plain?: boolean,
 *   headingLevel?: 'h2'|'h3',
 *   label?: string,
 *   id?: string,
 * }} props
 */
export function Section({
  title,
  description,
  action,
  footer,
  children,
  className,
  bodyClassName,
  flush = false,
  plain = false,
  headingLevel: Heading = 'h2',
  label,
  id,
}) {
  const headingId = useId();
  const labelled = title ? { 'aria-labelledby': headingId } : label ? { 'aria-label': label } : {};
  const region = title || label ? { role: 'region' } : {};

  if (plain) {
    return (
      <section id={id} {...labelled} {...region} className={cn('flex flex-col gap-3', className)}>
        {(title || action) && (
          <div className="flex items-end justify-between gap-3">
            <div className="flex min-w-0 flex-col gap-0.5">
              {title && (
                <Heading id={headingId} className="text-sm font-semibold">
                  {title}
                </Heading>
              )}
              {description && <p className="text-[0.8125rem] text-muted-foreground">{description}</p>}
            </div>
            {action}
          </div>
        )}
        {children}
        {footer && <p className="text-xs text-muted-foreground">{footer}</p>}
      </section>
    );
  }

  return (
    <section id={id} data-slot="section" {...labelled} {...region} className={cn('flex min-w-0 flex-col overflow-hidden rounded-xl border bg-card', className)}>
      {(title || action) && (
        <div className="flex min-h-12 flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b bg-muted/40 px-4 py-2.5">
          <div className="flex min-w-0 flex-col gap-0.5">
            {title && (
              <Heading id={headingId} className="text-sm font-semibold leading-5">
                {title}
              </Heading>
            )}
            {description && <p className="text-[0.8125rem] leading-5 text-muted-foreground">{description}</p>}
          </div>
          {action && <div className="flex shrink-0 items-center gap-2">{action}</div>}
        </div>
      )}
      <div className={cn('min-w-0 flex-1', !flush && 'p-4', bodyClassName)}>{children}</div>
      {footer && <div className="border-t px-4 py-2.5 text-xs text-muted-foreground">{footer}</div>}
    </section>
  );
}

/**
 * Compact list with hairline dividers. Stand-alone it is a card itself; inside a `Section` it fills the (flush) body.
 * Renders a `<ul>` (rows as `<li>`) unless `as` says otherwise.
 * @param {{ as?: any, className?: string, children: import('react').ReactNode } & Record<string, any>} props
 */
export function ItemList({ as: Comp = 'ul', className, children, ...props }) {
  return (
    <Comp
      data-slot="item-list"
      className={cn(
        'flex flex-col divide-y overflow-hidden rounded-xl border bg-card in-data-[slot=section]:rounded-none in-data-[slot=section]:border-0',
        className,
      )}
      {...props}
    >
      {children}
    </Comp>
  );
}

/**
 * Leading icon of a row: small bordered square, muted glyph (uniform icon colour).
 * @param {{ icon: import('react').ComponentType<any>, className?: string }} props
 */
export function ItemIcon({ icon: Icon, className }) {
  return (
    <span
      className={cn('flex size-8 shrink-0 items-center justify-center rounded-lg border bg-background text-muted-foreground [&>svg]:size-4', className)}
      aria-hidden="true"
    >
      <Icon />
    </span>
  );
}

/**
 * One row (~48 px): leading icon, title (14 px) + secondary line (13 px), trailing value/badges, chevron.
 * Pass `as={Link}` (+ router props), `as="a"` or `as="button"` for clickable rows.
 * @param {{
 *   as?: any,
 *   icon?: import('react').ComponentType<any>,
 *   leading?: import('react').ReactNode,
 *   title: import('react').ReactNode,
 *   subtitle?: import('react').ReactNode,
 *   detail?: import('react').ReactNode,
 *   accessory?: import('react').ReactNode,
 *   chevron?: boolean,
 *   destructive?: boolean,
 *   titleClassName?: string,
 *   className?: string,
 * } & Record<string, any>} props
 */
export function ItemRow({
  as: Comp = 'div',
  icon,
  leading,
  title,
  subtitle,
  detail,
  accessory,
  chevron,
  destructive = false,
  titleClassName,
  className,
  ...props
}) {
  const pressable = Comp !== 'div';
  return (
    <Comp
      data-slot="item-row"
      className={cn(
        'flex min-h-12 w-full items-center gap-3 px-4 py-2.5 text-left',
        pressable && 'cursor-pointer outline-offset-[-2px] transition-colors hover:bg-muted/50',
        destructive && 'text-destructive',
        className,
      )}
      {...(Comp === 'button' ? { type: 'button' } : {})}
      {...props}
    >
      {leading ?? (icon && <ItemIcon icon={icon} />)}
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className={cn('break-words text-sm font-medium leading-5', titleClassName)}>{title}</span>
        {subtitle && <span className="break-words text-[0.8125rem] leading-[1.125rem] text-muted-foreground">{subtitle}</span>}
      </span>
      {accessory && <span className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">{accessory}</span>}
      {detail !== undefined && detail !== null && <span className="shrink-0 text-[0.8125rem] text-muted-foreground tabular-nums">{detail}</span>}
      {(chevron ?? pressable) && <ChevronRight className="size-4 shrink-0 text-muted-foreground/60" aria-hidden="true" />}
    </Comp>
  );
}
